import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './App.css';
import MessageBoard from './components/MessageBoard/MessageBoard';

import './CourseDetail.css';
import SubmitUpdateForm from './components/SubmitUpdateForm';
import axiosInstance from './axiosConfig';
import LoadingSpinner from "./components/LoadingSpinner";
import Financials from "./Financials";
import AerialView from './AerialView';
import './AerialView.css';
import CourseImageGallery from './components/CourseImageGallery';
import './components/CourseImageGallery.css'

const CourseDetail = () => {
    // ADDED: clubSlug support
    const { id, clubId, clubSlug } = useParams();
    const navigate = useNavigate();
    const [clubData, setClubData] = useState(null);
    const [scorecards, setScorecards] = useState([]);
    const [selectedTeeType, setSelectedTeeType] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [parData, setParData] = useState(null);
    const [selectedCourse, setSelectedCourse] = useState('');
    const [activeTab, setActiveTab] = useState('Overview');
    const [showUpdateForm, setShowUpdateForm] = useState(false);
    const [cacheInfo, setCacheInfo] = useState(null);

    useEffect(() => {
        const fetchClubData = async () => {
            try {
                setLoading(true);
                setError(null);

                let apiUrl;
                let identifier;

                // UPDATED: Add slug support to existing fast logic
                if (clubSlug) {
                    // NEW: Slug-based access (uses your simple slug->clubId lookup)
                    apiUrl = `/club/slug/${clubSlug}/`;
                    identifier = clubSlug;
                } else if (clubId) {
                    // Direct club access (existing fast method)
                    apiUrl = `/clubs/${clubId}/complete/`;
                    identifier = clubId;
                } else if (id) {
                    // Backward compatibility (old course ID method)
                    if (!/^[0-9]+$/.test(id)) {
                        throw new Error('Invalid course ID format');
                    }
                    apiUrl = `/courses/${id}/club-data/`;
                    identifier = id;
                } else {
                    throw new Error('No valid ID provided');
                }

                console.log(`Fetching club data from: ${apiUrl}`);

                // SINGLE API CALL replaces all previous calls
                const response = await axiosInstance.get(apiUrl);
                const data = response.data;

                console.log('Club data received:', data);

                // Store the complete club data
                setClubData(data);

                // ADDED: Redirect old URLs to slug URLs for SEO
                if ((clubId || id) && !clubSlug && data.slug) {
                    navigate(`/club/${data.slug}`, { replace: true });
                    return;
                }

                // Store cache information for debugging/monitoring
                setCacheInfo({
                    cache_hit: data.cache_hit,
                    last_updated: data.last_updated,
                    api_endpoint: apiUrl
                });

                // Process scorecard data - same logic as before but using new data structure
                if (data.all_scorecards && data.all_scorecards.length > 0) {
                    setScorecards(data.all_scorecards);

                    // Group scorecards by heading to find the best course to display
                    const coursesGroupedByHeading = data.all_scorecards.reduce((acc, sc) => {
                        const heading = sc.heading || sc.course_name || data.name;
                        acc[heading] = acc[heading] || [];
                        acc[heading].push(sc);
                        return acc;
                    }, {});

                    // Find course with highest total yardage
                    const courseWithMaxYardage = Object.entries(coursesGroupedByHeading).reduce(
                        (maxCourse, [heading, scorecards]) => {
                            const yardageSum = scorecards.reduce((sum, sc) => sum + (sc.total_yardage || 0), 0);
                            return yardageSum > maxCourse.yardageSum ? { heading, yardageSum } : maxCourse;
                        },
                        { heading: '', yardageSum: 0 }
                    ).heading;

                    setSelectedCourse(courseWithMaxYardage);

                    // Set initial tee type to the one with highest yardage for this course
                    const maxYardageTeeType = data.all_scorecards
                        .filter(sc =>
                            (sc.heading || sc.course_name || data.name) === courseWithMaxYardage &&
                            sc.tee_type !== 'HCP' &&
                            sc.tee_type !== 'Par'
                        )
                        .reduce((max, sc) =>
                            (sc.total_yardage || 0) > (max.total_yardage || 0) ? sc : max,
                            { total_yardage: 0 }
                        );

                    if (maxYardageTeeType.tee_type) {
                        setSelectedTeeType(maxYardageTeeType.tee_type);
                    }

                    // Set par data
                    const parScorecard = data.all_scorecards.find(sc =>
                        sc.tee_type === 'Par' &&
                        (sc.heading || sc.course_name || data.name) === courseWithMaxYardage
                    );
                    setParData(parScorecard);
                } else {
                    console.warn('No scorecards found in club data');
                }

            } catch (err) {
                console.error('Error fetching club data:', err);
                if (err.response?.status === 404) {
                    setError('Club not found');
                } else {
                    setError(err.message || 'An error occurred while fetching data');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchClubData();
    }, [id, clubId, clubSlug, navigate]); // ADDED: clubSlug and navigate to dependencies

    const handleCourseChange = (e) => {
        const newSelectedCourse = e.target.value;
        setSelectedCourse(newSelectedCourse);

        // Find valid tee types for the selected course
        const validTeeTypes = scorecards.filter(sc =>
            (sc.heading || sc.course_name || clubData.name) === newSelectedCourse &&
            sc.tee_type !== 'Par' &&
            sc.tee_type !== 'HCP'
        );

        if (validTeeTypes.length > 0) {
            const maxYardageTeeType = validTeeTypes.reduce((max, sc) =>
                (sc.total_yardage || 0) > (max.total_yardage || 0) ? sc : max
            );
            setSelectedTeeType(maxYardageTeeType.tee_type);
        } else {
            setSelectedTeeType('');
        }

        // Update par data for the new course
        const parScorecard = scorecards.find(sc =>
            sc.tee_type === 'Par' &&
            (sc.heading || sc.course_name || clubData.name) === newSelectedCourse
        );
        setParData(parScorecard);
    };

    const handleTeeTypeChange = (e) => {
        setSelectedTeeType(e.target.value);
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    // ADDED: Format initiation fee properly (handle null as $0)
    const formatInitiationFee = (fee) => {
        if (fee === null || fee === undefined) return '$0';
        if (typeof fee === 'number') return `$${fee.toLocaleString()}`;
        return 'Contact Club';
    };

    const formatFee = (fee) => {
        if (fee === null || fee === undefined || fee === 'N/A') return 'Contact Club';
        if (typeof fee === 'number') return `$${fee.toLocaleString()}`;
        return 'Contact Club';
    };

    // Loading and error states
    if (loading) return <LoadingSpinner />;
    if (error) return <div className="error-message">Error: {error}</div>;
    if (!clubData) return <div className="not-found">Club data not found</div>;

    // Prepare data for rendering
    const course = clubData.primary_course || {};
    const summary = clubData.summary || '';

    // Get unique course names for the dropdown
    const uniqueCourses = [...new Set(scorecards.map(sc =>
        sc.heading || sc.course_name || clubData.name
    ))];

    // Get tee types for the selected course
    const teeTypes = scorecards
        .filter(sc =>
            (sc.heading || sc.course_name || clubData.name) === selectedCourse &&
            sc.tee_type !== 'Par'
        )
        .map(sc => sc.tee_type);

    return (
        <div className="course-detail">


            <div className="course-info-container">
                <div className="course-header-container">
                    <h1 className="course-name">{clubData.name}</h1>

                </div>
                <hr className="section-divider" />
                <hr className="divider" />

                <div className="course-info-2">
                    <p><strong>Address:</strong> {clubData.address || 'Address Unknown'}</p>
                    <p><strong>Phone:</strong> {clubData.phone_number || 'Phone Unknown'}</p>
                    <p><strong>Access Type:</strong> {course.access_type || 'Private'}</p>
                    <p><strong>Number of Holes:</strong> {course.number_of_holes || '18'}</p>

                    <hr className="section-divider" />

                    <div className="course-links">
                        {clubData.website && (
                            <a
                                href={clubData.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="visit-website"
                            >
                                Visit Website
                            </a>
                        )}
                        <span className="divider"> | </span>
                        <button
                            className="submit-update-button"
                            onClick={() => setShowUpdateForm(true)}
                        >
                            Submit Update
                        </button>
                    </div>
                </div>
            </div>

            {showUpdateForm && (
                <SubmitUpdateForm
                    courseId={clubData.club_id}
                    onClose={() => setShowUpdateForm(false)}
                />
            )}

            {/* Social Media Pills */}
            {(clubData.instagram_handle || clubData.x_handle) && (
                <div className="social-pills">
                    {clubData.instagram_handle && (
                        <a
                            href={`https://instagram.com/${clubData.instagram_handle}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="social-pill"
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                            </svg>
                            <span>@{clubData.instagram_handle}</span>
                        </a>
                    )}
                    {clubData.x_handle && (
                        <a
                            href={`https://x.com/${clubData.x_handle}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="social-pill"
                        >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                            </svg>
                            <span>@{clubData.x_handle}</span>
                        </a>
                    )}
                </div>
            )}

            {/* Course Image Gallery */}
            {clubData.address && (
                <div className="course-visuals-container">
                    <CourseImageGallery
                        courseName={clubData.name}
                        location={clubData.address}
                        imageCount={6}
                    />
                </div>
            )}

            {/* Aerial View */}
            {clubData.address && (
                <div className="aerial-view-container">
                    <AerialView
                        address={clubData.address}
                        orientation="landscape"
                        format="mp4_medium"
                    />
                </div>
            )}

            {/* Tab Navigation - Always show Financials tab */}
            <div className="tab-buttons">
                <button
                    className={activeTab === 'Overview' ? 'active' : ''}
                    onClick={() => handleTabChange('Overview')}
                >
                    Overview
                </button>
                <button
                    className={activeTab === 'Message Board' ? 'active' : ''}
                    onClick={() => handleTabChange('Message Board')}
                >
                    Message Board
                </button>
                <button
                    className={activeTab === 'Financials' ? 'active' : ''}
                    onClick={() => handleTabChange('Financials')}
                >
                    Financials
                </button>
            </div>

            {/* Overview Tab */}
            {activeTab === 'Overview' && (
                <>
                    {/* Club Summary */}
                    {summary && (
                        <div className="course-summary">
                            <h2>Club Overview</h2>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{summary}</ReactMarkdown>
                        </div>
                    )}

                    {/* Scorecard Section */}
                    <div className="scorecard-section">
                        <h2 className="scorecard-title">Scorecard</h2>

                        {/* Course Selector - only show if multiple courses */}
                        {uniqueCourses.length > 1 && (
                            <div className="course-selector">
                                <label htmlFor="course-select">Select Course:</label>
                                <select
                                    id="course-select"
                                    value={selectedCourse}
                                    onChange={handleCourseChange}
                                >
                                    {uniqueCourses.map((courseName, index) => (
                                        <option key={index} value={courseName}>
                                            {courseName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Tee Type Filter */}
                        <div className="filter-section-tee">
                            <label htmlFor="tee-type-filter">Filter by Tee Type:</label>
                            <select
                                id="tee-type-filter"
                                value={selectedTeeType}
                                onChange={handleTeeTypeChange}
                            >
                                {[...new Set(teeTypes)].map((type, index) => (
                                    <option key={index} value={type}>{type}</option>
                                ))}
                            </select>
                        </div>

                        {/* Scorecard Display */}
                        {scorecards.length > 0 ? (
                            [...new Map(
                                scorecards
                                    .filter(sc =>
                                        (sc.heading || sc.course_name || clubData.name) === selectedCourse &&
                                        sc.tee_type === selectedTeeType
                                    )
                                    .map(sc => [sc.tee_type, sc])
                            ).values()].map((sc, index) => {
                                const isNineHoleCourse = !sc.holes?.hole_10 && !sc[`hole_10`];

                                return (
                                    <div key={index} className="scorecard-entry">
                                        <div className="tee-info">
                                            <strong>{sc.heading || sc.course_name || sc.tee_type}</strong>
                                        </div>
                                        <div className="slope-rating">
                                            <strong>Slope Rating:</strong> {sc.slope_rating || "N/A"}
                                        </div>
                                        <table className="scorecard-table">
                                            <thead>
                                                <tr>
                                                    <th>Hole</th>
                                                    {[...Array(9)].map((_, i) => (
                                                        <th key={i}>{i + 1}</th>
                                                    ))}
                                                    <th>Out</th>
                                                    {!isNineHoleCourse && [...Array(9)].map((_, i) => (
                                                        <th key={i + 9}>{i + 10}</th>
                                                    ))}
                                                    {!isNineHoleCourse && <th>In</th>}
                                                    {!isNineHoleCourse && <th>Total</th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>{sc.tee_type}</td>
                                                    {[...Array(9)].map((_, i) => (
                                                        <td key={i}>
                                                            {sc.holes ? sc.holes[`hole_${i + 1}`] : sc[`hole_${i + 1}`]}
                                                        </td>
                                                    ))}
                                                    <td>{sc.out_total}</td>
                                                    {!isNineHoleCourse && [...Array(9)].map((_, i) => (
                                                        <td key={i + 9}>
                                                            {sc.holes ? sc.holes[`hole_${i + 10}`] : sc[`hole_${i + 10}`]}
                                                        </td>
                                                    ))}
                                                    {!isNineHoleCourse && <td>{sc.in_total}</td>}
                                                    {!isNineHoleCourse && <td>{sc.total_yardage}</td>}
                                                </tr>
                                                {parData && (
                                                    <tr>
                                                        <td>Par</td>
                                                        {[...Array(9)].map((_, i) => (
                                                            <td key={i}>
                                                                {parData.holes ? parData.holes[`hole_${i + 1}`] : parData[`hole_${i + 1}`]}
                                                            </td>
                                                        ))}
                                                        <td>{parData.out_total}</td>
                                                        {!isNineHoleCourse && [...Array(9)].map((_, i) => (
                                                            <td key={i + 9}>
                                                                {parData.holes ? parData.holes[`hole_${i + 10}`] : parData[`hole_${i + 10}`]}
                                                            </td>
                                                        ))}
                                                        {!isNineHoleCourse && <td>{parData.in_total}</td>}
                                                        {!isNineHoleCourse && <td>{parData.total_yardage}</td>}
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                );
                            })
                        ) : (
                            <p>No scorecard available</p>
                        )}
                    </div>
                </>
            )}

            {/* Message Board Tab */}
            {activeTab === 'Message Board' && (
                <div className="message-board-section">
                    <MessageBoard
                        club_id={clubData.club_id}
                        course_id={course.id} // Fallback for backward compatibility
                        clubName={clubData.name}
                    />
                </div>
            )}

            {/* Financials Tab - UPDATED: Better fee formatting */}
            {activeTab === 'Financials' && (
                <div className="financials-section">
                    {/* Membership Information - Show if available */}
                    {clubData.financial_data && Object.keys(clubData.financial_data).length > 0 && (
                        <div className="course-info-container">
                            <div className="course-header-container">
                                <h1 className="course-name" style={{textAlign: 'left'}}>Membership Estimate</h1>
                            </div>
                            <hr className="section-divider"/>
                            <hr className="divider"/>
                            <div className="course-info-2">
                                <p>
                                    <strong>Initiation Fee:</strong> {formatInitiationFee(clubData.financial_data.initiation_fee)}
                                </p>
                                {clubData.financial_data.annual_dues && (
                                    <p>
                                        <strong>Annual Dues:</strong> {formatFee(clubData.financial_data.annual_dues)}
                                    </p>
                                )}
                                {clubData.financial_data.monthly_dues && (
                                    <p>
                                        <strong>Monthly Dues:</strong> {formatFee(clubData.financial_data.monthly_dues)}
                                    </p>
                                )}
                                {clubData.financial_data.source && (
                                    <p>
                                        <strong>Source:</strong> {clubData.financial_data.source}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Tax Information - Lazy loaded when tab is clicked */}
                    {clubData?.club_id && (
                        <Financials clubId={clubData.club_id}/>
                    )}
                </div>
            )}
        </div>
    );
};

export default CourseDetail;